import React from 'react';
import { v4 } from 'uuid';
import DocumentItemsFormElement, { DocumentItemsFormElementSharedProps } from './DocumentItemsFormElement';
import {
  fromPurchaseOrderItem, Item, DocumentAvailability, poTierCosts,
  bindingLinePrice, suggestedResalePrice,
} from '../models/item';
import { PurchaseOrderItem } from '../models/purchaseOrder';
import usePricingSettings from '../hooks/usePricingSettings';
import { DEFAULT_PRICING_SETTINGS } from '../models/pricingSettings';

// item 554 — a PO is three-tiered for hardware (Chad: "we are a distributor"): DISTO (the
// buy price) → markup → GROUP PRICE (sell price) → markup → MSRP. It mirrors the invoice's
// layout, but the DISTO cost is ALWAYS editable (so labor / custom one-off lines set their
// price directly, ignoring the distributor tier) and the vendor PDF outputs DISTO only —
// group price + MSRP are on-screen margin reference.
const makeItemFromCatalog = (
  newItem: Item,
  category: string,
  defaultGroupMarkup = DEFAULT_PRICING_SETTINGS.groupMarkup,
  defaultSrpMarkup = DEFAULT_PRICING_SETTINGS.srpMarkup,
): PurchaseOrderItem => {
  // buy at the DISTRIBUTOR tier when the item carries one, else the dealer/base cost
  const tiers = poTierCosts(newItem);
  const distro = tiers.distributor ?? tiers.dealer ?? newItem.costEach;
  return {
    id: v4(),
    itemId: newItem.id,
    brand: newItem.brand,
    category,
    costEach: distro,
    // group price + MSRP ride along as reference (same math as the invoice)
    retailEach: bindingLinePrice(newItem),
    listPrice: suggestedResalePrice(newItem, defaultGroupMarkup, defaultSrpMarkup),
    isHardware: newItem.isHardware ?? false,
    description: newItem.description,
    quantity: 1,
    sku: newItem.sku,
    taxable: false,
    vendorId: newItem.vendorId,
  };
};

const PurchaseOrderItemsFormElement: React.FC<DocumentItemsFormElementSharedProps<PurchaseOrderItem>> = (props) => {
  const { pricing } = usePricingSettings(); // company default markups (shared with the invoice)
  const defaults = pricing ?? DEFAULT_PRICING_SETTINGS;
  return (
    <DocumentItemsFormElement<PurchaseOrderItem>
      categoryNames={props.categoryNames}
      items={props.items}
      setItems={props.setItems}
      categoryButtonHandler={props.categoryButtonHandler}
      activeCategory={props.activeCategory}
      categoryEditMode={props.categoryEditMode}
      editCategoryName={props.editCategoryName}
      setEditCategoryName={props.setEditCategoryName}
      onAddClose={props.onAddClose}
      isAddOpen={props.isAddOpen}
      showCost
      showRetail
      showMsrpPair
      showGrpPair
      defaultGrpMult={defaults.groupMarkup}
      defaultMsrpMult={defaults.srpMarkup}
      costAlwaysEditable
      costLabel="DISTO"
      totalLabel="GROUP PRICE"
      vendorsOnly
      availableOn={DocumentAvailability.PurchaseOrder}
      makeItemFromCatalog={(newItem, category) => makeItemFromCatalog(newItem, category, defaults.groupMarkup, defaults.srpMarkup)}
      toCatalogItem={fromPurchaseOrderItem}
    />
  );
};

export default PurchaseOrderItemsFormElement;
